import { CancelledReasonEnum } from "@redotech/redo-shopify-order-common/shopify-order";
import * as z from "zod";

const moneySchema_ = z.object({ amount: z.string(), currencyCode: z.string() });

const moneySetSchema_ = z.object({
  presentmentMoney: moneySchema_,
  shopMoney: moneySchema_,
});

const taxLineSchema_ = z.object({
  priceSet: moneySetSchema_,
  rate: z.string().nullable(),
  title: z.string().optional(),
});

const discountSchema_ = z.object({
  amountSet: moneySetSchema_,
  discountId: z.string(),
  discountTitle: z.string().optional(),
  discountCode: z.string().optional(),
});

const metafieldSchema_ = z.object({
  namespace: z.string(),
  key: z.string(),
  value: z.string(),
});

export interface CustomIntegrationMetafield extends z.infer<
  typeof metafieldSchema_
> {}

export interface TaxLine extends z.infer<typeof taxLineSchema_> {}

const baseCustomOrderTransactionSchema_ = z.object({
  paymentId: z.string(),
  amount: z.number(),
  currency: z.string(),
  createdDate: z.string(),
});

const authorizeNetTransactionSchema_ = baseCustomOrderTransactionSchema_.extend(
  {
    paymentGateway: z.literal("authorize_net"),
    additionalDetails: z.object({ creditCardLastFour: z.string().length(4) }),
  },
);

const genericTransactionSchema_ = baseCustomOrderTransactionSchema_
  .extend({ paymentGateway: z.string() })
  .catchall(z.unknown());

const customOrderTransactionSchema_ = z.union([
  authorizeNetTransactionSchema_,
  genericTransactionSchema_,
]);

export interface AuthorizeNetTransaction extends z.infer<
  typeof authorizeNetTransactionSchema_
> {}

export type CustomOrderTransaction = z.infer<
  typeof customOrderTransactionSchema_
>;
const customIntegrationFulfillmentSchema_ = z.object({
  id: z.string(),
  trackingNumbers: z
    .array(
      z.object({ carrier: z.string().nullish(), trackingNumber: z.string() }),
    )
    .optional()
    .default([]),
  trackingUrls: z.array(z.string()).optional().default([]),
  lineItems: z.array(z.object({ id: z.string(), quantity: z.number() })),
  shippingDate: z.string().nullish(),
  deliveryDate: z.string().nullish(),
});

export const CustomIntegrationFulfillmentSchema: z.ZodType<CustomIntegrationFulfillment> =
  customIntegrationFulfillmentSchema_;

export interface CustomIntegrationFulfillment extends z.infer<
  typeof customIntegrationFulfillmentSchema_
> {}

const customIntegrationRefundLineItemSchema_ = z.object({
  lineItemId: z.string(),
  quantity: z.number().int().positive(),
  subtotalSet: moneySetSchema_,
  totalTaxSet: moneySetSchema_,
});

const customIntegrationRefundAmountSchema_ = z.object({
  amountSet: moneySetSchema_,
  taxAmountSet: moneySetSchema_.optional(),
  reason: z.string().optional(),
});

const customIntegrationRefundSchema_ = z.object({
  id: z.string(),
  createdAt: z.string(),
  note: z.string().nullish(),
  lineItems: z
    .array(customIntegrationRefundLineItemSchema_)
    .optional()
    .default([]),
  shippingRefunds: z
    .array(customIntegrationRefundAmountSchema_)
    .optional()
    .default([]),
  orderLevelRefunds: z
    .array(customIntegrationRefundAmountSchema_)
    .optional()
    .default([]),
});

export const CustomIntegrationRefundSchema: z.ZodType<CustomIntegrationRefund> =
  customIntegrationRefundSchema_;

export interface CustomIntegrationRefundAmount extends z.infer<
  typeof customIntegrationRefundAmountSchema_
> {}

export interface CustomIntegrationRefund extends z.infer<
  typeof customIntegrationRefundSchema_
> {}

const customIntegrationOrderCoverageSchema = z.discriminatedUnion("purchased", [
  z.object({ purchased: z.literal(false), quoteId: z.string().optional() }),
  z.object({ purchased: z.literal(true), quoteId: z.string().min(1) }),
]);

export type CustomIntegrationOrderCoverage = z.infer<
  typeof customIntegrationOrderCoverageSchema
>;

const customIntegrationOrderSchema = z.object({
  id: z.string(),
  type: z.literal("order"),
  billingAddress: z
    .object({
      address1: z.string().nullish(),
      address2: z.string().nullish(),
      city: z.string().nullish(),
      company: z.string().nullish(),
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      provinceCode: z.string().nullish(),
      zip: z.string().nullish(),
      countryCode: z.string().nullish(),
      phone: z.string().nullish(),
    })
    .nullish(),
  salesChannel: z.string().nullish(),
  createdAt: z.string(),
  presentmentCurrency: z.string(),
  shopCurrency: z.string(),
  customer: z.object({
    email: z.email(),
    phone: z.string().nullish(),
    id: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
    tags: z.array(z.string()).optional().default([]),
  }),
  lineItems: z
    .array(
      z.object({
        id: z.string().min(1, "Line item id must not be empty"),
        quantity: z.number(),
        priceSet: moneySetSchema_, // THIS IS PRE-TAX and PRE Discount
        returnableQuantity: z.number().nullish(),
        title: z.string(),
        variantTitle: z.string().nullish(),
        vendor: z.string().nullish(),
        fulfillmentService: z.string().nullish(),
        productId: z.string(),
        variantId: z.string().nullish(),
        additionalProductId: z.string().nullish(),
        sku: z.string(),
        properties: z
          .array(z.object({ name: z.string(), value: z.string() }))
          .optional()
          .default([]),
        tags: z.array(z.string()).optional().default([]),
        metafields: z.array(metafieldSchema_).optional(),
        weight: z.object({ value: z.number(), unit: z.string() }).nullish(),
        image: z.string().nullish(),
        images: z
          .array(
            z.object({ url: z.string().nullish(), data: z.string().nullish() }),
          )
          .optional()
          .default([]),
        discounts: z // should take quantity into account
          .array(discountSchema_)
          .optional()
          .default([]),
        taxLines: z // This should take quantity into account
          .array(taxLineSchema_)
          .optional()
          .default([]),
      }),
    )
    .refine(
      (items) => new Set(items.map((item) => item.id)).size === items.length,
      { message: "Line item ids must be unique within an order" },
    ),
  // These are NOT the same as line item or shipping discounts
  orderDiscounts: z.array(discountSchema_),
  shippingLines: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      code: z.string().optional(),
      priceSet: moneySetSchema_, // THIS IS PRE-TAX and PRE_DISCOUNT
      discounts: z.array(discountSchema_).optional().default([]),
      taxLines: z.array(taxLineSchema_).optional().default([]),
    }),
  ),
  fulfillments: z.array(customIntegrationFulfillmentSchema_),
  shippingAddress: z.object({
    address1: z.string(),
    address2: z.string().nullish(),
    city: z.string(),
    company: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    provinceCode: z.string(),
    zip: z.string(),
    countryCode: z.string(),
    phone: z.string().nullish(),
  }),
  tags: z.array(z.string()).optional().default([]),
  metafields: z.array(metafieldSchema_).optional(),
  cancelledAt: z.string().nullish(),
  cancelReason: CancelledReasonEnum.nullish(),
  updatedAt: z.string(),
  transactions: z.array(customOrderTransactionSchema_).optional().default([]),
  refunds: z.array(customIntegrationRefundSchema_).optional().default([]),
  coverage: customIntegrationOrderCoverageSchema.optional(),
});

export const CustomIntegrationOrderSchema: z.ZodType<CustomIntegrationOrder> =
  customIntegrationOrderSchema;

export interface CustomIntegrationOrder extends z.infer<
  typeof customIntegrationOrderSchema
> {}

export interface MoneySet extends z.infer<typeof moneySetSchema_> {}

const lineItemShape =
  customIntegrationOrderSchema.shape.lineItems.element.shape;
const fulfillmentShape = customIntegrationFulfillmentSchema_.shape;

/** Fields of an existing order a caller may change, such as an order sync function.
 * Defaults are removed so an omitted field keeps the order's current value. */
const customIntegrationOrderChangesSchema = z.object({
  customer: z
    .object({ phone: customIntegrationOrderSchema.shape.customer.shape.phone })
    .optional(),
  lineItems: z
    .array(
      z.object({
        id: lineItemShape.id,
        tags: lineItemShape.tags.unwrap(),
        properties: lineItemShape.properties.unwrap(),
        returnableQuantity: lineItemShape.returnableQuantity,
        image: lineItemShape.image,
        images: lineItemShape.images.unwrap(),
      }),
    )
    .optional(),
  fulfillments: z
    .array(
      z.object({
        id: fulfillmentShape.id,
        lineItems: fulfillmentShape.lineItems,
        shippingDate: fulfillmentShape.shippingDate,
        trackingNumbers: fulfillmentShape.trackingNumbers.unwrap(),
      }),
    )
    .optional(),
});

export const CustomIntegrationOrderChangesSchema: z.ZodType<CustomIntegrationOrderChanges> =
  customIntegrationOrderChangesSchema;

export interface CustomIntegrationOrderChanges extends z.infer<
  typeof customIntegrationOrderChangesSchema
> {}
